import { Component, OnInit, inject } from '@angular/core';
import { BaseComponent } from '../../../core/components/base/base.component';
import { Select } from '../../models/select.model';
import { LocalStorageService } from '../../services/local-storage.service';
import { NotifyService } from '../../services/notify.service';
import { StateService } from '../../services/state.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { Tenant } from '../../models/tenant';
import { safeUrlAfterCompanySwitch, switchCompanyAndGo } from '../../utils/company-switch';
import { CompanySwitcherModalComponent } from './company-switcher-modal.component';
import { CompanyTriggerComponent } from './company-trigger.component';
import { GroupBrandService } from '../../../core/services/group-brand.service';

// Campos de identidade que a lista de empresas traz fresca da API.
const IDENTITY_FIELDS = [
  'brandColor', 'logoWideUrl', 'groupDisplayName', 'avatarUrl', 'fantasyName', 'name', 'companyGroupName',
] as const satisfies ReadonlyArray<keyof Tenant>;

@Component({
  selector: 'app-company-select',
  templateUrl: './company-select.component.html',
  styleUrl: './company-select.component.scss',
  imports: [CompanyTriggerComponent],
  host: { '[class.company-select--brand]': '!!identity()?.color' },
})
export class CompanySelectComponent extends BaseComponent implements OnInit {
  public value?: number;
  public companies: Array<Tenant> = [];
  private _lastValue?: number;
  private switcherOpen = false;

  private readonly groupBrand = inject(GroupBrandService);
  public readonly identity = this.groupBrand.identity;
  // URL do logo que falhou ao carregar: guardada por URL, então trocar de empresa/logo tenta de novo.
  public failedLogoUrl: string | null = null;

  constructor(
    private notifyService: NotifyService,
    private router: Router,
    private userService: UserService,
    private modalService: NgbModal
  ) {
    super();
  }

  ngOnInit(): void {
    this.value = this.stateService.contextAtual || undefined;
    this._lastValue = this.value;

    this.getCompanies();

    this.subscriptions.add(
      this.stateService.tenant$.subscribe(tenant => {
        if (tenant && this.value !== tenant.id) {
          this.value = tenant.id;
          this._lastValue = tenant.id;
        }
      })
    );
  }

  private getCompanies() {
    this.userService.GetUserCompanies().subscribe({
      next: (response: Array<Tenant>) => {
        this.companies = response;

        if (this.companies.length > 0) {
          const currentTenantId = this.stateService.contextAtual;
          const currentValid = this.companies.find(c => c.id === currentTenantId);

          if (!currentValid) {
            this.stateService.setTenant(this.companies[0]);
          } else {
            this.reconcileIdentity(currentValid);
          }
        } else {
          this.stateService.limparContexto();
        }
      },
      error: (err) => {
        console.error('Error loading companies:', err);
      }
    });
  }

  // A sessão guarda o tenant no localStorage e o StateService o restaura ao recarregar sem reler a
  // API: cor, logo ou nome novos do grupo nunca chegariam a uma sessão aberta. A lista fresca corrige.
  private reconcileIdentity(fresh: Tenant): void {
    const active = this.stateService.tenant$.value;
    if (!active || active.id !== fresh.id) return;
    const changed = IDENTITY_FIELDS.filter((key) => (fresh[key] ?? null) !== (active[key] ?? null));
    if (changed.length === 0) return;
    this.groupBrand.refresh(Object.fromEntries(changed.map((key) => [key, fresh[key]])) as Partial<Tenant>);
  }

  public get current(): Tenant | undefined {
    return this.companies.find((c) => c.id === this.value);
  }

  public get currentLabel(): string {
    return this.identity()?.companyName || this.current?.fantasyName || this.current?.name || '';
  }

  // Iniciais da marca quando o grupo não tem ícone: as do grupo (como no protótipo), senão as da empresa.
  public get markName(): string {
    return this.identity()?.groupName || this.currentLabel;
  }

  // Logo solto da faixa com marca: o horizontal, senão o ícone; some se não carregou.
  public get brandLogo(): string | null {
    const url = this.identity()?.logoUrl ?? null;
    return url && url !== this.failedLogoUrl ? url : null;
  }

  // Ícone do grupo no gatilho abaixo de xl (lá não entra o logo horizontal).
  public get avatarUrl(): string {
    return this.stateService.tenant$.value?.avatarUrl || this.current?.avatarUrl || '';
  }

  // Seletor grande (busca por empresa, grupo ou CNPJ). Ele devolve a empresa JÁ CONFIRMADA (a
  // confirmação é o segundo passo do próprio seletor); recarregar é daqui (changeCompany).
  public openSwitcher(): void {
    // Um seletor por vez: clique duplo no gatilho abria dois modais empilhados.
    if (this.companies.length <= 1 || this.switcherOpen) return;
    this.switcherOpen = true;

    const ref = this.modalService.open(CompanySwitcherModalComponent, {
      size: 'lg',
      // Celular: tela cheia. Como janela flutuante ele ficava espremido (margem dos quatro lados,
      // lista cortada no meio) — seletor de empresa no celular é uma TELA, não um popup.
      fullscreen: 'md',
      scrollable: false,
      modalDialogClass: 'company-switcher-dialog',
      backdropClass: 'company-switcher-backdrop',
      // Entrada própria (ver CompanySwitcherModalComponent.ngAfterViewInit): o fade do Bootstrap
      // começava no mesmo quadro em que a lista é montada, o quadro mais caro da abertura, e no
      // Firefox a animação pulava o começo (parecia travada).
      animation: false,
    });
    ref.componentInstance.companies = this.companies;
    ref.componentInstance.currentId = this.value;

    ref.result.then(
      (target: Tenant) => {
        this.switcherOpen = false;
        this.value = target.id;
        this.changeCompany();
      },
      // Esc / clique fora: nada muda.
      (): void => { this.switcherOpen = false; },
    );
  }

  public changeCompany() {
    if (!this.value) return;

    const target = this.companies.find(c => c.id === this.value);
    if (!target) return;

    // Dentro de um registro, recarregar na MESMA URL quebra ("registro não encontrado":
    // o id é da empresa anterior) — a troca desembarca na listagem do módulo.
    switchCompanyAndGo(this.stateService, target,
      safeUrlAfterCompanySwitch(window.location.pathname, window.location.search));
  }
}
